import { $blockRowNavigate } from "@/stores"
import { TableFilterOption } from "@/types"
import { useClickOutside } from "@/utils"
import { Column, Table, TableMeta } from "@tanstack/react-table"
import { CirclePlus, Search } from "lucide-react"
import { useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { CSSTransition } from "react-transition-group"

interface DataTableFacetedFilter<TData, TValue> {
    column?: Column<TData, TValue>,
    title: string,
    options: TableFilterOption[],
    table: Table<TData>,
    filterId: string
}

export function DataTableFacetedFilter<TData, TValue>({
    column,
    title,
    options,
    table,
    filterId
}: DataTableFacetedFilter<TData, TValue>) {
    const { type } = table.options.meta as TableMeta<TData>;
    const selectedValues = new Set(column?.getFilterValue() as string[])
    const [isListOpen, setIsListOpen] = useState(false);
    const [filterOptions, setFilterOptions] = useState(options);
    const [searchParams, setSearchParams] = useSearchParams();
    const areaRef = useRef(null);

    function saveFiltersToQueryParams() {
        const filtersArray = Array.from(selectedValues);
        const params = new URLSearchParams(searchParams);
        if (filtersArray.length === 0) params.delete(filterId.toLowerCase());
        else params.set(filterId.toLowerCase(), filtersArray.toString());
        setSearchParams(params);
    }

    useClickOutside(areaRef, () => {
        setIsListOpen(false);
        if (isListOpen) {
            saveFiltersToQueryParams();
            setTimeout(() => {
                $blockRowNavigate.set(false);
            }, 200);
            $blockRowNavigate.set(true);
        }
    }, column?.id.toString());


    return (

        <div className="facet-filter">
            <button className={`filter-button ${column?.id}`} onClick={() => {

                if (type === "Invoices") $blockRowNavigate.set(true);
                setIsListOpen(!isListOpen)
            }}>
                <CirclePlus width={14} height={14} />
                {title.charAt(0).toUpperCase() + title.slice(1)}
            </button>
            <div>
                <CSSTransition in={isListOpen}
                    timeout={200}
                    classNames="fade"
                    exit={false}
                    unmountOnExit>

                    <div className="facet-filter__options-container" ref={areaRef}>
                        <div className="facet-filter__search-container">
                            <Search width={14} height={14} />
                            <input
                                className="facet-filter__filter-search"
                                placeholder={`${title.charAt(0).toUpperCase() + title.slice(1)}...`}
                                onChange={(e) => {
                                    const query = e.target.value.toLowerCase();
                                    if (query.length === 0) setFilterOptions(options);
                                    const filtered = options.filter((item) => item.value.toLowerCase().includes(query));
                                    setFilterOptions(filtered);
                                }}
                                onFocus={() => {
                                    setFilterOptions(options);
                                }} />
                        </div>

                        <div className="facet-filter__options-list">
                            {filterOptions.map((option, i) => {
                                const isSelected = selectedValues.has(option.value)
                                return (
                                    <div className="facet-filter__filter-option-container" key={i}>
                                        <button
                                            className="facet-filter__filter-option"
                                            key={option.value}
                                            onClick={() => {
                                                table.toggleAllPageRowsSelected(false);
                                                if (isSelected) {
                                                    selectedValues.delete(option.value)
                                                } else {
                                                    selectedValues.add(option.value)
                                                }
                                                const filterValues = Array.from(selectedValues);
                                                column?.setFilterValue(
                                                    filterValues.length ? filterValues : undefined
                                                )
                                            }}
                                        >
                                            <div className="facet-filter__option-text">
                                                <input type="checkbox" checked={selectedValues.has(option.value)} readOnly />
                                                <span>{option.text}</span>
                                            </div>
                                        </button>
                                    </div>
                                )
                            })}

                            {filterOptions.length === 0 && <div className="facet-filter__empty-container">
                                <span>No options.</span>
                            </div>}
                        </div>
                        {selectedValues.size > 0 && (
                            <div className="facet-filter__clear-filters-container">
                                <div className="facet-filter__filter-buttons">
                                    <button className="facet-filter__save-filters" onClick={() => {
                                        saveFiltersToQueryParams();
                                        setIsListOpen(false);
                                    }}>Save</button>
                                    <button
                                        className="facet-filter__clear-filters"
                                        onClick={() => column?.setFilterValue(undefined)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </CSSTransition>
            </div>
        </div>
    )
}